import { useCallback, useEffect, useState } from 'react'
import { getDefaultTileFilterSelections } from '../components/AppPage2/utils'
import { InferFormValues, JsonFormSchemaV2 } from '../components/component-library/FormComponent/types'

/**
 * Custom hook for managing tile filter selections
 *
 * This hook abstracts the common filtering logic used across various components
 * like CardsComponent, ChartComponent, DataTableComponent, and FormComponent.
 *
 * @param filters - The filters object from the component data
 * @returns An object containing filter selections and the filter change handler
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useTileFilterSelections(filters: { properties?: JsonFormSchemaV2['properties'] } = {}) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [tileFilterSelections, setTileFilterSelections] = useState<Record<string, any>[]>([])
  const [properties, setProperties] = useState<JsonFormSchemaV2['properties']>([])
  const [isDefaultsSet, setIsDefaultsSet] = useState(false)

  // Update properties when filters change
  useEffect(() => {
    if (filters?.properties) {
      setProperties(filters.properties)
      setIsDefaultsSet(false)
    }
  }, [filters?.properties])

  // Set default filter selections when properties are available
  useEffect(() => {
    if (isDefaultsSet || !properties || properties.length === 0) {
      return
    }
    const defaultTileFilterSelections = getDefaultTileFilterSelections(properties)
    setTileFilterSelections(defaultTileFilterSelections)
    setIsDefaultsSet(true)
  }, [properties, isDefaultsSet])

  // Handler for filter changes
  const filterChangeHandler = useCallback(
    (data: InferFormValues<JsonFormSchemaV2>) =>
      setTileFilterSelections(
        Object.entries(data).map(([key, value]) => ({
          [key]: value,
        }))
      ),
    []
  )

  return {
    tileFilterSelections,
    filterChangeHandler,
  }
}
